import type {
  Direction,
  Disruption,
  DisruptionActivePeriod,
  GeoJsonPoint,
  JourneyLeg,
  Line,
  LineAssets,
  LineLinks,
  LineRef,
  LineTimetableLink,
  Operator,
  PlaceSearchResult,
  Stop,
  StopLinks,
  Timetable,
  TimetableJourney,
  TimetableVisit,
  TimetableWaypoint,
  Visit,
  VisitRestriction,
} from '../types';
import type {
  HalDirection,
  HalDisruption,
  HalDisruptionActivePeriod,
  HalDisruptionEmbeds,
  HalEmbeddedOperator,
  HalHrefLink,
  HalLine,
  HalLineAssetsResponse,
  HalLineLink,
  HalPlaceSearchEntry,
  HalPlanLeg,
  HalStopLink,
  HalStopResourceLinks,
  HalTimetableLink,
  HalTimetableResponse,
  HalVisit,
} from './hal';

export function mapStop(hal: HalStopLink): Stop | null {
  const commonName = normaliseString(hal.commonName);

  if (!commonName || !isGeoJsonPoint(hal.location)) {
    return null;
  }

  const atcoCode = normaliseString(hal.atcoCode) ?? normaliseString(hal.id);
  if (!atcoCode) {
    return null;
  }

  const links = mapStopLinks(hal._links);

  return {
    atcoCode,
    commonName,
    localityName: hal.localityName,
    stopType: normaliseString(hal.stopType),
    location: hal.location,
    indicator: normaliseString(hal.indicator),
    bearing: normaliseString(hal.bearing),
    ...(links ? { links } : {}),
  };
}

export function mapPlaceSearchResult(
  hal: HalPlaceSearchEntry,
): PlaceSearchResult | null {
  const id = normaliseString(hal.id);
  const commonName = normaliseString(hal.commonName);

  if (!id || !commonName || !isGeoJsonPoint(hal.location)) return null;

  return {
    id,
    type: hal.type === 'stop' ? 'stop' : 'place',
    commonName,
    localityName: hal.localityName,
    distance: typeof hal.distance === 'number' ? hal.distance : undefined,
    location: hal.location,
    indicator: normaliseString(hal.indicator),
    bearing: normaliseString(hal.bearing),
  };
}

export function mapHrefLinks(links?: HalHrefLink[]): string[] | undefined {
  const hrefs = links?.flatMap((link) => {
    const href = normaliseString(link.href);
    return href ? [href] : [];
  });

  return hrefs?.length ? hrefs : undefined;
}

export function mapLineRefs(links?: HalLineLink[]): LineRef[] | undefined {
  const mapped = links?.flatMap((link) => {
    const lineRef = mapLineRef(link);
    return lineRef ? [lineRef] : [];
  });

  return mapped?.length ? mapped : undefined;
}

export function mapVisit(hal: HalVisit): Visit | null {
  const destinationName = normaliseString(hal.destinationName);

  if (!destinationName) {
    return null;
  }

  const line = hal._links?.['transmodel:line']
    ? (mapLineRef(hal._links['transmodel:line']) ?? undefined)
    : undefined;

  const sources = hal.sources?.map((source) => source.trim()).filter(Boolean);

  return {
    direction: normaliseString(hal.direction),
    cancelled: hal.cancelled,
    destinationName,
    aimedArrivalTime: normaliseString(hal.aimedArrivalTime),
    aimedDepartureTime: normaliseString(hal.aimedDepartureTime),
    expectedArrivalTime: normaliseString(hal.expectedArrivalTime),
    expectedDepartureTime: normaliseString(hal.expectedDepartureTime),
    isRealTime: hal.isRealTime,
    displayTime: normaliseString(hal.displayTime),
    restriction: mapVisitRestriction(hal.restriction),
    sources: sources?.length ? sources : undefined,
    line,
  };
}

export function mapLine(hal: HalLine): Line | null {
  const name = normaliseString(hal.name) ?? normaliseString(hal.title);

  if (!name) {
    return null;
  }

  const id =
    normaliseString(hal.id) ??
    normaliseString(hal._links?.self?.href) ??
    `line:${name}`;

  const directions = hal._embedded?.['transmodel:direction']?.flatMap(
    (direction) => {
      const mapped = mapDirection(direction);
      return mapped ? [mapped] : [];
    },
  );

  return {
    id,
    name,
    title: hal.title,
    description: normaliseString(hal.description),
    colors: hal.colors,
    mode: normaliseString(hal.mode),
    alerts: mapDisruptions(extractEmbeddedDisruptions(hal._embedded)),
    startDate: normaliseString(hal.startDate),
    endDate: normaliseString(hal.endDate),
    directions: directions?.length ? directions : undefined,
    operator: mapOperator(hal._embedded?.['transmodel:operator']),
    links: hal._links ? mapLineLinks(hal._links) : undefined,
  };
}

export function mapLeg(hal: HalPlanLeg): JourneyLeg {
  const legAlerts = [
    ...(hal.alerts ?? []),
    ...extractEmbeddedDisruptions(hal._embedded),
  ];

  const leg: JourneyLeg = {
    mode: hal.mode,
    distance: hal.distance,
    paces: hal.paces,
    departureDateTime: hal.departureDateTime,
    arrivalDateTime: hal.arrivalDateTime,
    alerts: mapDisruptions(legAlerts),
    path: hal.path,
    interlined: hal.interlined,
    expectedDepartureDateTime: hal.expectedDepartureDateTime,
    expectedState: hal.expectedState,
    expectedStateAffectsTransfers: hal.expectedStateAffectsTransfers,
    notes: hal.notes,
  };

  if (hal.lineName) leg.lineName = hal.lineName;
  if (hal.headsign) leg.headsign = hal.headsign;

  if (hal._links?.['transmodel:line']) {
    leg.line = mapLineRef(hal._links['transmodel:line']) ?? undefined;
  }

  leg.timetableLink = mapTimetableLink(hal._links?.timetable);
  leg.departuresHref = normaliseString(hal._links?.departures?.href);

  if (hal._links?.['naptan:stop']) {
    leg.stops = hal._links['naptan:stop'].flatMap((stop) => {
      const mappedStop = mapStop(stop);

      if (!mappedStop) {
        return [];
      }

      return [
        {
          atcoCode: mappedStop.atcoCode,
          commonName: mappedStop.commonName,
          stopType: mappedStop.stopType,
          location: mappedStop.location,
          links: mappedStop.links,
        },
      ];
    });
  }

  const operator = mapJourneyLegOperator(hal._embedded);
  if (operator) {
    const code = normaliseString(operator.code);
    const name = normaliseString(operator.name);

    if (!code || !name) {
      return leg;
    }

    leg.operator = {
      code,
      name,
      attributes: operator.attributes,
    };
  }

  return leg;
}

export function mapDisruptions(alerts: HalDisruption[]): Disruption[] {
  return alerts.flatMap((alert) => {
    if (!alert.id) {
      return [];
    }

    return [
      {
        id: alert.id,
        header: alert.header,
        description: alert.description,
        cause: alert.cause,
        effect: alert.effect,
        resolved: alert.resolved,
        activePeriods: mapDisruptionActivePeriods(alert.activePeriods),
      },
    ];
  });
}

export function extractEmbeddedDisruptions(
  embedded: HalDisruptionEmbeds | undefined,
): HalDisruption[] {
  if (!embedded) {
    return [];
  }

  return [
    ...(embedded.alerts ?? []),
    ...(embedded.alert ?? []),
    ...(embedded.disruption ?? []),
    ...(embedded['transmodel:alert'] ?? []),
  ];
}

export function mapTimetableResponse(data: HalTimetableResponse): Timetable {
  const waypoints: TimetableWaypoint[] = (
    data._embedded?.['timetable:waypoint'] ?? []
  ).flatMap((wp) => {
    const stopSource =
      wp._embedded?.stop ??
      wp._embedded?.['naptan:stop'] ??
      wp._links?.['naptan:stop'];
    const stop = stopSource ? mapStop(stopSource) : null;

    return stop
      ? [
          {
            major: wp.major,
            stop,
          },
        ]
      : [];
  });

  const journeys: TimetableJourney[] = (
    data._embedded?.['timetable:journey'] ?? []
  ).map((journey, index) => ({
    id:
      normaliseString(journey.id) ??
      `journey-${typeof journey.sequenceNumber === 'number' ? journey.sequenceNumber : index}`,
    sequenceNumber:
      typeof journey.sequenceNumber === 'number'
        ? journey.sequenceNumber
        : index,
    visits: (journey._embedded?.['timetable:visit'] ?? []).map(
      (visit): TimetableVisit => ({
        aimedArrivalTime: visit.aimedArrivalTime,
        aimedDepartureTime: visit.aimedDepartureTime,
        waypointIndex: parseWaypointIndex(
          visit._links?.['timetable:waypoint']?.href,
        ),
        restriction: mapVisitRestriction(visit.restriction),
      }),
    ),
    line: journey._links?.['transmodel:line']
      ? (mapLineRef(journey._links['transmodel:line']) ?? undefined)
      : undefined,
    noticeCodes: journey.noticeCodes ?? [],
  }));

  const direction = data._embedded?.['transmodel:direction']
    ? mapDirection(data._embedded['transmodel:direction'])
    : undefined;

  const lines = data._embedded?.['transmodel:line']?.flatMap((line) => {
    const mapped = mapLineRef({
      href: line._links?.self?.href,
      name: line.name,
      title: line.title ?? undefined,
      description: line.description,
      operator: line._embedded?.['transmodel:operator']?.code,
      colors: line.colors,
      mode: line.mode,
    });

    return mapped ? [mapped] : [];
  });

  const notices = (data.notices ?? []).flatMap((notice) => {
    const noticeCode = normaliseString(notice.code);
    const noticeText = normaliseString(notice.text);

    return noticeCode && noticeText ? [{ noticeCode, noticeText }] : [];
  });

  return {
    waypoints,
    journeys,
    direction: direction ?? undefined,
    lines,
    switchLink: data._links?.switch?.href,
    notices: notices.length > 0 ? notices : undefined,
  };
}

const LINE_ASSET_CATEGORIES = ['timetable', 'map', 'fares'] as const;

export function mapLineAssetsResponse(
  data: HalLineAssetsResponse,
  resolveHref: (href: string) => string,
): LineAssets {
  const assets: LineAssets = { timetable: [], map: [], fares: [] };

  for (const [rel, links] of Object.entries(data._links ?? {})) {
    if (!Array.isArray(links)) continue;

    // Qualified rels such as `timetable.major-stops` belong to their base
    // category, so match on the category prefix rather than the exact key.
    const category = LINE_ASSET_CATEGORIES.find(
      (candidate) => candidate === rel || rel.startsWith(`${candidate}.`),
    );

    if (category) {
      assets[category].push(
        ...links.map((link) => ({ ...link, href: resolveHref(link.href) })),
      );
    }
  }

  return assets;
}

function mapStopLinks(hal?: HalStopResourceLinks): StopLinks | undefined {
  if (!hal) {
    return undefined;
  }

  const links: StopLinks = {
    self: hal.self?.href,
    visits: hal.visits?.href,
    alerts: hal.alerts?.href,
    lines: hal.lines?.href,
    shapes: hal.shapes?.href,
  };

  return Object.values(links).some(Boolean) ? links : undefined;
}

function isGeoJsonPoint(value: unknown): value is GeoJsonPoint {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as {
    type?: unknown;
    coordinates?: unknown;
  };

  if (candidate.type !== 'Point' || !Array.isArray(candidate.coordinates)) {
    return false;
  }

  if (candidate.coordinates.length !== 2) return false;

  const [lng, lat] = candidate.coordinates;
  return typeof lng === 'number' && typeof lat === 'number';
}

function normaliseString(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function mapLineRef(hal: HalLineLink): LineRef | null {
  const name = normaliseString(hal.name) ?? normaliseString(hal.title);

  if (!name) {
    return null;
  }

  return {
    href: normaliseString(hal.href),
    name,
    title: normaliseString(hal.title),
    description: normaliseString(hal.description),
    colors: hal.colors,
    operator: normaliseString(hal.operator),
    mode: normaliseString(hal.mode),
  };
}

function mapTimetableLink(
  hal?: HalTimetableLink,
): LineTimetableLink | undefined {
  const href = normaliseString(hal?.href);

  if (!href) {
    return undefined;
  }

  return {
    href,
    line: normaliseString(hal?.line) ?? normaliseString(hal?.name),
    direction: normaliseString(hal?.direction),
    date: normaliseString(hal?.date),
    operator: normaliseString(hal?.operator),
  };
}

function mapLineLinks(hal: NonNullable<HalLine['_links']>): LineLinks {
  return {
    self: normaliseString(hal.self?.href),
    assets: normaliseString(hal.assets?.href),
    shapes: normaliseString(hal.shapes?.href),
    stops: normaliseString(hal.stops?.href),
    alerts: normaliseString(hal.alerts?.href),
    changes: normaliseString(hal.changes?.href),
    timetable: mapTimetableLink(hal.timetable),
  };
}

function mapOperator(operator?: {
  code?: string;
  name?: string;
}): Operator | undefined {
  const code = normaliseString(operator?.code);
  const name = normaliseString(operator?.name);

  return code && name ? { code, name } : undefined;
}

function mapDirection(hal: HalDirection): Direction | null {
  const name = normaliseString(hal.name);

  if (!name) {
    return null;
  }

  return {
    name,
    origin: normaliseString(hal.origin),
    destination: normaliseString(hal.destination),
    vias: hal.vias,
  };
}

function mapJourneyLegOperator(
  embedded: HalPlanLeg['_embedded'],
): HalEmbeddedOperator | undefined {
  const operators = [
    ...toOperatorArray(embedded?.['transmodel:operator']),
    ...toOperatorArray(embedded?.['transxchange:operator']),
  ];

  return operators.find((operator) =>
    Boolean(normaliseString(operator.code) && normaliseString(operator.name)),
  );
}

function toOperatorArray(
  value: HalEmbeddedOperator | HalEmbeddedOperator[] | undefined,
): HalEmbeddedOperator[] {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

function mapDisruptionActivePeriods(
  periods?: HalDisruptionActivePeriod[],
): DisruptionActivePeriod[] | undefined {
  if (!periods?.length) {
    return undefined;
  }

  return periods.map((period) => ({
    start: period.start,
    end: period.end,
    timeRangeDisplay: period.time_range_display,
  }));
}

function mapVisitRestriction(
  restriction: string | undefined,
): VisitRestriction | undefined {
  return restriction === 'pickUpOnly' || restriction === 'setDownOnly'
    ? restriction
    : undefined;
}

function parseWaypointIndex(href: string | undefined): number {
  if (!href) return -1;
  const match = /^timetable:waypoint:(\d+)$/.exec(href);
  return match ? parseInt(match[1], 10) : -1;
}
